import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Trees, Target, Heart, Users, Leaf, Sparkles, Settings, Linkedin } from "lucide-react";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export function PlanDetailsTab() {
  const navigate = useNavigate();
  const [planInfo, setPlanInfo] = useState(null);
  const [ictTeamMembers, setIctTeamMembers] = useState([]);
  const [masterplanners, setMasterplanners] = useState([]);
  const [projectLeads, setProjectLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plan info
        const planSnapshot = await getDocs(collection(db, 'planInfo'));
        if (planSnapshot.docs.length > 0) {
          setPlanInfo(planSnapshot.docs[0].data());
        }

        // Fetch ICT team members and sort by creation date (oldest first)
        const ictMembersSnapshot = await getDocs(collection(db, 'ictTeamMembers'));
        const ictMembersData = ictMembersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort by createdAt timestamp, oldest first
        ictMembersData.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime; // Ascending order (oldest first)
        });
        setIctTeamMembers(ictMembersData);

        // Fetch masterplanners
        const masterplannersSnapshot = await getDocs(collection(db, 'masterplanners'));
        const masterplannersData = masterplannersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        masterplannersData.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime;
        });
        setMasterplanners(masterplannersData);

        // Fetch project leads
        const projectLeadsSnapshot = await getDocs(collection(db, 'projectLeads'));
        const projectLeadsData = projectLeadsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        projectLeadsData.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime;
        });
        setProjectLeads(projectLeadsData);
      } catch (err) {
        console.error('Error fetching plan details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className='text-white text-center py-8'>Loading...</div>;

  const goals = planInfo?.goals?.split('\n').filter(g => g.trim()) || [];

  return (
    <div className="section-container">
      <div className="section-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-subtitle">Project Details</h3>
          {user && (
            <Button 
              onClick={() => navigate('/manage-plan-details')}
              className='bg-primary-500 hover:bg-primary-600 text-white flex items-center gap-2'
            >
              <Settings className='h-4 w-4' />
              Manage
            </Button>
          )}
        </div>

        {/* Project Leads */}
        {projectLeads.length > 0 && (
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-3">Project Lead</h4>
            <div className="flex flex-wrap gap-3">
              {projectLeads.map((person, index) => (
                <span key={person.id} className="text-white flex items-center gap-1">
                  {person.linkedinUrl ? (
                    <a 
                      href={person.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-primary-400 transition-colors flex items-center gap-1"
                    >
                      {person.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </a>
                  ) : (
                    <>
                      {person.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </>
                  )}
                  {index < projectLeads.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Masterplanners */}
        {masterplanners.length > 0 && (
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-3">Masterplanners</h4>
            <div className="flex flex-wrap gap-3">
              {masterplanners.map((person, index) => (
                <span key={person.id} className="text-white flex items-center gap-1">
                  {person.linkedinUrl ? (
                    <a 
                      href={person.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-primary-400 transition-colors flex items-center gap-1"
                    >
                      {person.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </a>
                  ) : (
                    <>
                      {person.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </>
                  )}
                  {index < masterplanners.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ICT Team */}
        {ictTeamMembers.length > 0 && (
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-3">Volunteer ICT Team</h4>
            <div className="flex flex-wrap gap-3">
              {ictTeamMembers.map((member, index) => (
                <span key={member.id} className="text-white flex items-center gap-1">
                  {member.imageUrl ? (
                    <a 
                      href={member.imageUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-primary-400 transition-colors flex items-center gap-1"
                    >
                      {member.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </a>
                  ) : (
                    <>
                      {member.name}
                      <span className="inline-flex items-center justify-center bg-white rounded-sm p-0.5 ml-0.5">
                        <img src="/images/InBug-Black.png" alt="LinkedIn" className="h-2 w-2" />
                      </span>
                    </>
                  )}
                  {index < ictTeamMembers.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="section-card">
        <h3 className="section-subtitle">Detailed Masterplan Information</h3>
        <p className="section-text">
          {planInfo?.detailedInfo || 'Comprehensive masterplan details, specifications, and documentation will be available here once finalized.'}
        </p>
      </div>
    </div>
  );
}